import { useEffect } from 'react';
import {
  isSfxAppearance,
  playPrSfx,
  setPrSfxAppearance,
  syncAmbientHum,
  unlockPrSfx,
} from './pacificRimSfx';

function isTypingTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) {
    return true;
  }
  return Boolean(target.closest('input, textarea, [contenteditable="true"]'));
}

function isTypingKey(e) {
  const key = e?.key;
  if (typeof key !== 'string') return false;
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  if (key === 'Backspace' || key === 'Delete' || key === 'Enter') {
    return true;
  }
  if (key === 'Tab' || key === 'Escape') return false;
  if (key.length === 1) return true;
  return false;
}

function isInteractiveTarget(target) {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      'button, a[href], [role="button"], [role="tab"], [role="menuitem"], .MuiButtonBase-root, .MuiIconButton-root, .MuiToggleButton-root, [data-pr-sfx]'
    )
  );
}

/** PR / Star Wars / Star Trek: Klicks, Hover, Tippen + Ambient. */
export function usePacificRimUiSounds(appearance) {
  setPrSfxAppearance(appearance);

  useEffect(() => {
    setPrSfxAppearance(appearance);
  }, [appearance]);

  useEffect(() => {
    if (!isSfxAppearance(appearance)) {
      syncAmbientHum();
      return;
    }

    const onPointerDown = () => unlockPrSfx();
    const onClickCapture = () => {
      playPrSfx('click');
    };
    const onPointerOverCapture = (e) => {
      if (e.pointerType === 'touch') return;
      if (!isInteractiveTarget(e.target)) return;
      playPrSfx('hover');
    };
    const onKeyDownCapture = (e) => {
      if (e.repeat) return;
      if (!isTypingTarget(e.target)) return;
      if (!isTypingKey(e)) return;
      unlockPrSfx();
      playPrSfx('key');
    };
    const onVisibility = () => {
      if (document.hidden) syncAmbientHum();
      else {
        unlockPrSfx();
        syncAmbientHum();
      }
    };

    window.addEventListener('pointerdown', onPointerDown, {
      capture: true,
      passive: true,
    });
    window.addEventListener('click', onClickCapture, true);
    window.addEventListener('pointerover', onPointerOverCapture, true);
    window.addEventListener('keydown', onKeyDownCapture, true);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('click', onClickCapture, true);
      window.removeEventListener('pointerover', onPointerOverCapture, true);
      window.removeEventListener('keydown', onKeyDownCapture, true);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [appearance]);
}
